import React, { useCallback } from 'react';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Ati } from '@atlassian/ari/ati';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { fg } from '@atlassian/jira-feature-gating';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import type { MenuItem } from '@atlassian/universal-create/types';
import { CREATE_WITH_ROVO_MENU_ITEM } from './constants.tsx';

const createWithAi = defineMessage({
	id: 'jira-ai.create-with-ai.create-with-ai',
	defaultMessage: 'Work items with Rovo',
	description: 'The text for the create work items with Rovo item in the dropdown',
});

const createWithAiNew = defineMessage({
	id: 'jira-ai.create-with-ai.create-with-ai-new',
	defaultMessage: 'Create with Rovo',
	description: 'The text for the create work items with Rovo item in the dropdown',
});

const betaLozenge = defineMessage({
	id: 'jira-ai.create-with-ai.beta-lozenge',
	defaultMessage: 'Beta',
	description: 'The text for the beta lozenge in the dropdown',
});

type UseCreateWithAiItemProps = {
	onItemClick: (isOpen: boolean) => void;
};

/**
 * Entrypoint for the Jira AI First Creation Experience.
 *
 * @param onCreateClick
 * @param experienceTracker
 * @returns
 */
export function useCreateWithAiItem({ onItemClick }: UseCreateWithAiItemProps): MenuItem {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();

	const onClick = useCallback(() => {
		onItemClick(true);
		experienceTracker.start({
			name: ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
			id: ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
			attributes: {
				product: 'jira',
			},
		});
	}, [experienceTracker, onItemClick]);

	return {
		ati: Ati.parse('ati:cloud:jira:rovo'),
		children: fg('jira_planner_uc_enabled') ? (
			<Inline space="space.100">
				{formatMessage(createWithAiNew)}
				<Lozenge appearance="new">{formatMessage(betaLozenge)}</Lozenge>
			</Inline>
		) : (
			<Inline alignBlock="center" spread="space-between">
				{formatMessage(createWithAi)}
				<Lozenge appearance="new">{formatMessage(betaLozenge)}</Lozenge>
			</Inline>
		),
		testId: CREATE_WITH_ROVO_MENU_ITEM,
		elemBefore: RovoIcon,
		onClick,
	};
}
